import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/usePropertyQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { PropertySection } from "web_myplann/Portfolio/Components/PropertySections/PropertySection";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePropertyTitle } from "web_myplann/Portfolio/Hooks/usePropertyTitle";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

export const PropertyDetailPage: FC = () => {
	const { id } = useParams<{ id: string }>();
	const { t } = useResource();
	const theme = useTheme();

	const { data: property, isLoading } = usePropertyQuery(Number(id));

	const title = usePropertyTitle(property, t);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [id]);

	const currencySign = t(Resources.Common.CurrencySign);

	const showEmpty = !isLoading && hasNoBusinessData(property);

	return (
		<MyPlannLayout
			topBar={<NavigationHeader title={title} defaultRoute={DEFAULT_ROUTE} />}
			disableBottomPadding
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<StyledCenteredStack spacing={2} px={5}>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<Stack textAlign="center" gap={0.4}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.empty)}
						</Typography>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
						</Typography>
					</Stack>
				</StyledCenteredStack>
			) : (
				<Stack pt={2}>
					{property && (
						<PropertySection
							property={property}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
